import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Progress } from "./components/ui/progress";
import vsMark from "./assets/vs-mark.svg";
import { DISCIPLINES, type AppState, type Discipline, type DisciplineState, type GameMode, type Match, type Standing, type Team, type TeamCostume, type TeamStatus } from "./domain";
import { cn } from "./lib/utils";
import "./styles.css";

type View = "dashboard" | "matches" | "teams" | "ranking" | "ranking-v2";
type RankingSortKey = "teamId" | "rank" | "played" | "wins" | "losses" | "bonus" | "points" | "winrate";
type SortDirection = "asc" | "desc";
type RandomFillPreset = 50 | 80 | 99;

const blankState: AppState = { teams: [], matches: [], standings: [], statuses: [], disciplineStates: [], teamCostumes: [], event: { startedAt: null, dashboardMessage: "", dashboardMessageVisible: true, gameMode: "mario-kart" } };

const disciplineIconsBase: Record<Discipline, string> = {
  Kicker: "sports_soccer",
  "Mario Kart": "directions_car",
  Dart: "adjust",
  Billard: "counter_8",
  Tischtennis: "sports_tennis"
};

const navIcons: Record<View, string> = {
  dashboard: "monitoring",
  matches: "table",
  teams: "groups",
  ranking: "emoji_events",
  "ranking-v2": "leaderboard"
};
const showDashboardTeams = false;

type RankingStanding = Standing & {
  team?: Team;
  costumeBonus: boolean;
  rankingPoints: number;
};

function disciplineIcon(discipline: Discipline, gameMode: GameMode) {
  return discipline === "Mario Kart" && gameMode === "halligalli" ? "notifications" : disciplineIconsBase[discipline];
}

function disciplineLabel(discipline: Discipline, gameMode: GameMode) {
  return discipline === "Mario Kart" && gameMode === "halligalli" ? "Halligalli" : discipline;
}

function Icon({ name, className }: { name: string; className?: string }) {
  return <span className={cn("material-symbols-rounded text-[18px] leading-none", className)} aria-hidden="true">{name}</span>;
}

function App() {
  const [state, setState] = useState<AppState>(blankState);
  const [view, setView] = useState<View>("dashboard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gameMode = state.event.gameMode;

  async function refresh() {
    setError(null);
    const response = await fetch("/api/state");
    if (!response.ok) throw new Error("Status konnte nicht geladen werden.");
    setState(await response.json());
  }

  useEffect(() => {
    refresh()
      .catch((nextError) => setError(nextError.message))
      .finally(() => setLoading(false));
    const timer = window.setInterval(() => refresh().catch(() => undefined), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  async function patchMatch(matchId: number, winnerTeamId: number | null, bonus: boolean) {
    setError(null);
    let previousState: AppState | null = null;
    setState((current) => {
      previousState = current;
      return applyOptimisticMatchResult(current, matchId, winnerTeamId, bonus);
    });
    const response = await fetch(`/api/matches/${matchId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ winnerTeamId, bonus })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      if (previousState) setState(previousState);
      setError(payload.error ?? "Begegnung konnte nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  async function patchDisciplineState(discipline: Discipline, activeMatchId: number | null, nextMatchId: number | null) {
    setError(null);
    let previousState: AppState | null = null;
    setState((current) => {
      previousState = current;
      return applyOptimisticDisciplineState(current, discipline, activeMatchId, nextMatchId);
    });
    const response = await fetch("/api/discipline-state", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ discipline, activeMatchId, nextMatchId })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      if (previousState) setState(previousState);
      setError(payload.error ?? "Spielplanung konnte nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  async function saveTeams(teams: Team[]) {
    setError(null);
    const response = await fetch("/api/teams", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(teams)
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      setError(payload.error ?? "Teams konnten nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  async function resetResults() {
    if (!window.confirm("Alle Ergebnisse wirklich zurücksetzen?")) return;
    const response = await fetch("/api/reset", { method: "POST" });
    setState(await response.json());
  }

  async function randomizeResults(preset: RandomFillPreset) {
    if (!window.confirm(`Zufallsdaten für ${preset === 99 ? "99% (1 Spiel offen)" : `${preset}%`} erzeugen? Bestehende Ergebnisse werden überschrieben.`)) return;
    setError(null);
    const response = await fetch("/api/random-results", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ preset })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      setError(payload.error ?? "Zufallsdaten konnten nicht erzeugt werden.");
      return;
    }
    setState(payload);
  }

  async function startEvent() {
    setError(null);
    const response = await fetch("/api/event/start", { method: "POST" });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      setError(payload.error ?? "Pentathlon konnte nicht gestartet werden.");
      return;
    }
    setState(payload);
  }

  async function resetClock() {
    setError(null);
    const response = await fetch("/api/event/reset-clock", { method: "POST" });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      setError(payload.error ?? "Uhr konnte nicht zurückgesetzt werden.");
      return;
    }
    setState(payload);
  }

  async function updateDashboardMessage(message: string, visible: boolean) {
    setError(null);
    const response = await fetch("/api/event/message", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ message, visible })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      setError(payload.error ?? "Dashboard-Nachricht konnte nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  async function setGameMode(nextMode: GameMode) {
    setError(null);
    let previousState: AppState | null = null;
    setState((current) => {
      previousState = current;
      return { ...current, event: { ...current.event, gameMode: nextMode } };
    });
    const response = await fetch("/api/event/game-mode", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ gameMode: nextMode })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      if (previousState) setState(previousState);
      setError(payload.error ?? "Spiel konnte nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  async function setTeamCostume(teamId: number, enabled: boolean) {
    setError(null);
    let previousState: AppState | null = null;
    setState((current) => {
      previousState = current;
      return { ...current, teamCostumes: applyTeamCostume(current.teamCostumes, teamId, enabled) };
    });
    const response = await fetch(`/api/team-costumes/${teamId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ enabled })
    });
    const payload = (await response.json()) as AppState & { error?: string };
    if (!response.ok) {
      if (previousState) setState(previousState);
      setError(payload.error ?? "Team-Kostüm konnte nicht gespeichert werden.");
      return;
    }
    setState(payload);
  }

  const progress = useMemo(() => {
    const done = state.matches.filter((match) => match.winnerTeamId !== null).length;
    return { done, total: state.matches.length, percent: state.matches.length ? Math.round((done / state.matches.length) * 100) : 0 };
  }, [state.matches]);

  return (
    <Shell view={view} setView={setView} progress={progress} startedAt={state.event.startedAt} loading={loading} startEvent={startEvent} resetResults={resetResults} resetClock={resetClock} randomizeResults={randomizeResults} gameMode={gameMode} setGameMode={setGameMode}>
      {error ? <div className="mb-3 rounded-lg bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">{error}</div> : null}
      {loading ? <Card><CardContent className="p-4 text-sm text-muted-foreground">Pentathlon wird geladen...</CardContent></Card> : null}
      {!loading && view === "dashboard" ? <Dashboard state={state} updateDashboardMessage={updateDashboardMessage} gameMode={gameMode} /> : null}
      {!loading && view === "matches" ? (
        <Matches
          state={state}
          patchMatch={patchMatch}
          patchDisciplineState={patchDisciplineState}
          setView={setView}
          progress={progress}
          startedAt={state.event.startedAt}
          loading={loading}
          startEvent={startEvent}
          resetResults={resetResults}
          resetClock={resetClock}
          randomizeResults={randomizeResults}
          gameMode={gameMode}
          setGameMode={setGameMode}
        />
      ) : null}
      {!loading && view === "teams" ? <Teams state={state} saveTeams={saveTeams} /> : null}
      {!loading && view === "ranking" ? (
        <RankingPage
          state={state}
          progress={progress}
          startedAt={state.event.startedAt}
          loading={loading}
          setView={setView}
          startEvent={startEvent}
          resetResults={resetResults}
          resetClock={resetClock}
          randomizeResults={randomizeResults}
          gameMode={gameMode}
          setGameMode={setGameMode}
          setTeamCostume={setTeamCostume}
        />
      ) : null}
      {!loading && view === "ranking-v2" ? (
        <RankingV2Page
          state={state}
          progress={progress}
          startedAt={state.event.startedAt}
          loading={loading}
          setView={setView}
          startEvent={startEvent}
          resetResults={resetResults}
          resetClock={resetClock}
          randomizeResults={randomizeResults}
          gameMode={gameMode}
          setGameMode={setGameMode}
          setTeamCostume={setTeamCostume}
        />
      ) : null}
    </Shell>
  );
}

function Shell({
  children,
  view,
  setView,
  progress,
  startedAt,
  loading,
  startEvent,
  resetResults,
  resetClock,
  randomizeResults,
  gameMode,
  setGameMode
}: {
  children: React.ReactNode;
  view: View;
  setView: (view: View) => void;
  progress: { done: number; total: number; percent: number };
  startedAt: string | null;
  loading: boolean;
  startEvent: () => void;
  resetResults: () => void;
  resetClock: () => void;
  randomizeResults: (preset: RandomFillPreset) => void;
  gameMode: GameMode;
  setGameMode: (gameMode: GameMode) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  const clock = useMemo(() => makeClock(startedAt, now, progress.done, progress.total), [startedAt, now, progress.done, progress.total]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {view !== "matches" && view !== "ranking" && view !== "ranking-v2" ? <nav className="sticky top-0 z-10 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between gap-4 px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="whitespace-nowrap text-sm font-semibold">Pentathlon 2026</span>
            {!startedAt ? (
              <Button size="sm" className="h-8" onClick={startEvent}>
                <Icon name="play_arrow" /> Pentathlon starten
              </Button>
            ) : null}
          </div>
          <div className="hidden w-full max-w-xl md:block">
            <div className="mb-1 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
              {startedAt ? (
                <div className="flex min-w-0 items-center gap-3 truncate">
                  <span>Start {clock.startTime}</span>
                  <span>{clock.elapsed} gespielt</span>
                  <span>{loading ? "..." : `${progress.done}/${progress.total} Spiele`}</span>
                  <span>{clock.projection}</span>
                </div>
              ) : (
                <span>Pentathlon noch nicht gestartet</span>
              )}
              <span>{progress.percent}%</span>
            </div>
            <Progress value={progress.percent} />
          </div>
          <div className="flex items-center gap-2">
            <AppMenu
              activeView={view}
              title="Menü"
              setView={setView}
              progress={progress}
              startedAt={startedAt}
              loading={loading}
              startEvent={startEvent}
              resetResults={resetResults}
              resetClock={resetClock}
              randomizeResults={randomizeResults}
              gameMode={gameMode}
              setGameMode={setGameMode}
              compact
            />
          </div>
        </div>
      </nav> : null}
      <main className={cn("px-5", view === "matches" || view === "ranking" || view === "ranking-v2" ? "h-screen overflow-hidden pb-0" : view === "dashboard" ? "h-[calc(100vh-76px)] overflow-hidden pb-3" : "pb-5")}>{children}</main>
    </div>
  );
}

function makeClock(startedAt: string | null, now: number, done: number, total: number) {
  if (!startedAt) return { startTime: "-", elapsed: "00:00", projection: "Ende offen" };
  const start = new Date(startedAt);
  const startMs = start.getTime();
  const elapsedMs = Math.max(0, now - startMs);
  const projectedTotalMs = done > 0 && elapsedMs >= 60_000 ? (elapsedMs / done) * total : null;
  const remainingMs = projectedTotalMs === null ? null : Math.max(0, projectedTotalMs - elapsedMs);
  const end = projectedTotalMs === null ? null : new Date(startMs + projectedTotalMs);
  return {
    startTime: formatTime(start),
    elapsed: formatDuration(elapsedMs),
    projection: end && remainingMs !== null ? `Ende ca. ${formatTime(end)} · noch ${formatDuration(remainingMs)}` : "Ende offen"
  };
}

function formatTime(date: Date) {
  return date.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number) {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function Dashboard({ state, updateDashboardMessage, gameMode }: { state: AppState; updateDashboardMessage: (message: string, visible: boolean) => void; gameMode: GameMode }) {
  const [sort, setSort] = useState<{ key: RankingSortKey; direction: SortDirection }>({ key: "teamId", direction: "asc" });
  const rows = useMemo(() => {
    return state.standings
      .map((standing) => ({
        ...standing,
        team: getTeam(state.teams, standing.teamId),
        winrate: standing.played ? Math.round((standing.wins / standing.played) * 100) : 0
      }))
      .sort((left, right) => {
        const direction = sort.direction === "asc" ? 1 : -1;
        const leftValue = left[sort.key];
        const rightValue = right[sort.key];
        return (Number(leftValue) - Number(rightValue) || left.teamId - right.teamId) * direction;
      });
  }, [sort, state.standings, state.teams]);
  const rankingRows = useMemo(() => {
    return [...state.standings]
      .map((standing) => ({ ...standing, team: getTeam(state.teams, standing.teamId) }))
      .sort((left, right) => left.teamId - right.teamId);
  }, [state.standings, state.teams]);

  function changeSort(key: RankingSortKey) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc"
    }));
  }

  return (
    <section className="flex h-full min-h-0 flex-col pt-1">
      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(300px,0.36fr)_minmax(820px,1.8fr)]">
        <div className={cn("grid min-h-0 gap-3", showDashboardTeams ? "grid-rows-[auto_minmax(0,1fr)]" : "grid-rows-[minmax(0,1fr)]")}>
          {showDashboardTeams ? <Card className="gap-0 overflow-hidden py-0">
            <CardHeader className="px-4 pb-0.5 pt-2.5">
              <CardTitle className="flex items-center gap-2 text-base"><Icon name="groups" /> Teams</CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-2">
              <div className="overflow-hidden rounded-lg">
                <table className="w-full table-fixed border-separate border-spacing-0 text-[9px] [&_tr]:h-4">
                  <thead>
                    <tr className="text-left text-[9px] text-muted-foreground">
                      <RankHead label="ID" sortKey="teamId" sort={sort} onSort={changeSort} className="w-8" />
                      <th className="w-24 min-w-24 px-1.5 py-0.5 font-medium">Team</th>
                      <RankHead label="R" sortKey="rank" sort={sort} onSort={changeSort} />
                      <RankHead label="S" sortKey="played" sort={sort} onSort={changeSort} />
                      <RankHead label="W" sortKey="wins" sort={sort} onSort={changeSort} />
                      <RankHead label="L" sortKey="losses" sort={sort} onSort={changeSort} />
                      <RankHead label="+" sortKey="bonus" sort={sort} onSort={changeSort} />
                      <RankHead label="G" sortKey="points" sort={sort} onSort={changeSort} />
                      <RankHead label="W%" sortKey="winrate" sort={sort} onSort={changeSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((standing) => (
                      <tr key={standing.teamId} className="hover:bg-muted/35">
                        <td className="border-t border-black/6 px-1.5 py-0 font-medium tabular-nums">{standing.teamId}</td>
                        <td className="w-24 min-w-24 truncate whitespace-nowrap border-t border-black/6 px-1.5 py-0">{standing.team?.name}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.rank}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.played}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.wins}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.losses}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.bonus}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 font-medium tabular-nums">{standing.points}</td>
                        <td className="border-t border-black/6 px-1.5 py-0 tabular-nums">{standing.winrate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card> : null}
          <Card className="min-h-0 gap-0 overflow-hidden py-0">
            <CardHeader className="px-4 pb-0.5 pt-2.5">
              <CardTitle className="flex items-center gap-2 text-base"><Icon name="groups" /> Teams</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col gap-2 px-3 pb-3">
              {rankingRows.map((standing, index) => (
                <RankingCard key={standing.teamId} standing={standing} index={index} />
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="grid min-h-0 grid-cols-4 grid-rows-4 gap-2">
          {state.teams.map((team) => {
            const standing = state.standings.find((item) => item.teamId === team.id);
            const status = state.statuses.find((item) => item.teamId === team.id);
            const current = state.matches.find((match) => match.id === status?.currentMatchId);
            return (
              <Card key={team.id} className="min-h-0 gap-0 overflow-hidden py-0">
                <CardHeader className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 px-3 pb-2 pt-2.5 text-left">
                  <div className="min-w-0">
                    <CardTitle className="flex min-w-0 items-center justify-start gap-1.5 text-left text-sm">
                      <Badge variant="outline">{team.id}</Badge>
                      <span className="truncate">{team.name}</span>
                    </CardTitle>
                    <p className="mt-1 truncate text-left text-xs text-muted-foreground">{team.memberA} & {team.memberB}</p>
                  </div>
                  <div className="rounded-md bg-muted/55 px-2 py-1 text-center tabular-nums">
                    <div className="text-sm font-semibold leading-tight">#{standing?.rank ?? "-"}</div>
                    <div className="text-[9px] font-medium uppercase leading-tight text-muted-foreground">Rank</div>
                  </div>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col px-3 pb-3 pt-2 text-center text-sm">
                  <div className="min-h-0 flex-1">
                    <MatchLine label="Spielt" match={current} teams={state.teams} ownId={team.id} gameMode={gameMode} />
                  </div>
                  <div className="hidden grid-cols-4 gap-1 pt-1.5 text-center text-[10px]">
                    <Metric label="Spiele" value={`${standing?.played ?? 0}/15`} />
                    <Metric label="Siege" value={standing?.wins ?? 0} />
                    <Metric label="Punkte" value={standing?.points ?? 0} />
                    <Metric label="Rank" value={`#${standing?.rank ?? "-"}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
      <div className="hidden">
        <DashboardMessage event={state.event} updateDashboardMessage={updateDashboardMessage} />
      </div>
    </section>
  );
}

function DashboardMessage({
  event,
  updateDashboardMessage
}: {
  event: AppState["event"];
  updateDashboardMessage: (message: string, visible: boolean) => void;
}) {
  const [draft, setDraft] = useState(event.dashboardMessage);
  const [focused, setFocused] = useState(false);
  const fontSize = messageFontSize(draft);

  useEffect(() => {
    if (!focused) setDraft(event.dashboardMessage);
  }, [event.dashboardMessage, focused]);

  useEffect(() => {
    if (!focused || draft === event.dashboardMessage) return;
    const timer = window.setTimeout(() => updateDashboardMessage(draft, event.dashboardMessageVisible), 600);
    return () => window.clearTimeout(timer);
  }, [draft, event.dashboardMessage, event.dashboardMessageVisible, focused, updateDashboardMessage]);

  function toggleVisible() {
    updateDashboardMessage(draft, !event.dashboardMessageVisible);
  }

  return (
    <Card className="relative min-h-0 overflow-hidden py-0">
      <Button
        variant="ghost"
        size="sm"
        className="absolute right-3 top-3 z-10 h-8 rounded-md bg-background/80 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground"
        onClick={toggleVisible}
        title={event.dashboardMessageVisible ? "Nachricht ausblenden" : "Nachricht anzeigen"}
      >
        <Icon name={event.dashboardMessageVisible ? "visibility" : "visibility_off"} />
      </Button>
      {event.dashboardMessageVisible ? (
        <textarea
          className="h-full w-full resize-none overflow-hidden rounded-xl bg-transparent px-5 py-3 pr-16 font-semibold leading-tight outline-none placeholder:text-muted-foreground/45"
          style={{ fontSize }}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            if (draft !== event.dashboardMessage) updateDashboardMessage(draft, event.dashboardMessageVisible);
          }}
          placeholder="Nachricht für alle eingeben..."
        />
      ) : (
        <div className="grid h-full place-items-center px-5 py-4 text-sm text-muted-foreground">
          Nachricht ausgeblendet
        </div>
      )}
    </Card>
  );
}

function messageFontSize(message: string) {
  const length = message.trim().length;
  if (length <= 35) return "36px";
  if (length <= 80) return "30px";
  if (length <= 140) return "24px";
  if (length <= 230) return "20px";
  if (length <= 340) return "17px";
  return "15px";
}

function applyOptimisticMatchResult(state: AppState, matchId: number, winnerTeamId: number | null, bonus: boolean): AppState {
  const target = state.matches.find((match) => match.id === matchId);
  if (!target) return state;

  const matches = state.matches.map((match) => {
    if (match.id !== matchId) {
      if (winnerTeamId !== null && bonus && match.discipline === target.discipline && match.bonusTeamId === winnerTeamId) {
        return { ...match, bonusTeamId: null };
      }
      return match;
    }
    return {
      ...match,
      winnerTeamId,
      bonusTeamId: winnerTeamId !== null && bonus ? winnerTeamId : null,
      completedAt: winnerTeamId === null ? null : match.completedAt ?? new Date().toISOString()
    };
  });
  const disciplineStates = state.disciplineStates.map((item) => (
    winnerTeamId !== null && item.activeMatchId === matchId
      ? { ...item, activeMatchId: null }
      : item
  ));
  return applyOptimisticState(state, matches, disciplineStates);
}

function applyOptimisticDisciplineState(state: AppState, discipline: Discipline, activeMatchId: number | null, nextMatchId: number | null): AppState {
  const disciplineStates = state.disciplineStates.map((item) => (
    item.discipline === discipline ? { ...item, activeMatchId, nextMatchId } : item
  ));
  return applyOptimisticState(state, state.matches, disciplineStates);
}

function applyTeamCostume(teamCostumes: TeamCostume[], teamId: number, enabled: boolean) {
  const exists = teamCostumes.some((item) => item.teamId === teamId);
  if (exists) return teamCostumes.map((item) => item.teamId === teamId ? { ...item, enabled } : item);
  return [...teamCostumes, { teamId, enabled }];
}

function applyOptimisticState(state: AppState, matches: Match[], disciplineStates: DisciplineState[]): AppState {
  return {
    ...state,
    matches,
    disciplineStates,
    standings: makeClientStandings(state.teams, matches),
    statuses: makeClientStatuses(state.teams, matches, disciplineStates)
  };
}

function makeClientStandings(teams: Team[], matches: Match[]): Standing[] {
  const standings = teams.map((team) => {
    const teamMatches = matches.filter((match) => match.teamAId === team.id || match.teamBId === team.id);
    const completed = teamMatches.filter((match) => match.winnerTeamId !== null);
    const wins = completed.filter((match) => match.winnerTeamId === team.id).length;
    const bonus = completed.filter((match) => match.bonusTeamId === team.id).length;
    return {
      teamId: team.id,
      rank: 0,
      played: completed.length,
      wins,
      losses: completed.length - wins,
      bonus,
      points: wins + bonus,
      remaining: teamMatches.length - completed.length
    };
  });

  standings.sort((left, right) => right.points - left.points || right.wins - left.wins || left.losses - right.losses || left.teamId - right.teamId);
  standings.forEach((standing, index) => {
    standing.rank = index + 1;
  });
  return standings;
}

function makeClientStatuses(teams: Team[], matches: Match[], disciplineStates: DisciplineState[]): TeamStatus[] {
  return teams.map((team) => {
    const activeMatches = disciplineStates
      .map((item) => matches.find((match) => match.id === item.activeMatchId))
      .filter((match): match is Match => match !== undefined)
      .filter((match) => match.teamAId === team.id || match.teamBId === team.id);
    const nextMatches = disciplineStates
      .map((item) => matches.find((match) => match.id === item.nextMatchId))
      .filter((match): match is Match => match !== undefined)
      .filter((match) => match.teamAId === team.id || match.teamBId === team.id);
    return {
      teamId: team.id,
      currentMatchId: activeMatches[0]?.id ?? null,
      nextMatchId: nextMatches[0]?.id ?? null
    };
  });
}

function RankHead({
  label,
  sortKey,
  sort,
  onSort,
  className
}: {
  label: string;
  sortKey: RankingSortKey;
  sort: { key: RankingSortKey; direction: SortDirection };
  onSort: (key: RankingSortKey) => void;
  className?: string;
}) {
  return (
    <th className={cn("px-1 py-0.5 font-medium", className)}>
      <button className={cn("inline-flex items-center gap-0.5 hover:text-foreground", sort.key === sortKey && "text-foreground")} onClick={() => onSort(sortKey)}>
        {label}<Icon name="unfold_more" className={cn("text-[11px] opacity-35", sort.key === sortKey && "opacity-65")} />
      </button>
    </th>
  );
}

function RankingCard({
  standing,
  index
}: {
  standing: Standing & { team?: Team };
  index: number;
}) {
  return (
    <div className="grid min-h-0 flex-1 grid-cols-[1.7rem_1fr_auto] items-center gap-1.5 rounded-md bg-muted/45 px-2 py-0 text-[11px]">
      <span className="flex size-6 items-center justify-center rounded-full bg-background text-[11px] font-semibold tabular-nums text-foreground shadow-sm">
        {index + 1}
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold">{standing.team?.name ?? `Team ${standing.teamId}`}</div>
        <div className="truncate text-[10px] text-muted-foreground">{standing.played}/15 Spiele · {standing.wins} Siege · {standing.bonus} Pluspunkte</div>
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold leading-tight tabular-nums">{standing.points}</div>
        <div className="text-[10px] leading-tight text-muted-foreground">Pkt.</div>
      </div>
    </div>
  );
}

function MatchLine({ label, match, teams, ownId, gameMode }: { label: string; match?: Match; teams: Team[]; ownId: number; gameMode: GameMode }) {
  if (!match) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-1 rounded-md bg-muted/70 bg-[repeating-linear-gradient(135deg,transparent_0,transparent_13px,rgba(0,0,0,0.028)_13px,rgba(0,0,0,0.028)_17px)] px-2 py-1.5 text-center">
        <b className="block truncate text-base leading-tight text-muted-foreground">Pause</b>
      </div>
    );
  }
  const opponentId = match.teamAId === ownId ? match.teamBId : match.teamAId;
  const opponent = getTeam(teams, opponentId);
  const isCurrent = label === "Aktuell" || label === "Spielt";
  return (
    <div className={cn("flex h-full min-h-0 flex-col items-center justify-center gap-1 rounded-md px-4 py-1.5 text-center", isCurrent ? "bg-green-100" : "bg-blue-100")}>
      <b className="flex max-w-full min-w-0 items-center justify-center gap-1.5 truncate text-center text-base leading-tight">
        <Icon name={disciplineIcon(match.discipline, gameMode)} className="text-[19px]" />
        <span className="truncate">{disciplineLabel(match.discipline, gameMode)}</span>
      </b>
      <img src={vsMark} alt="" className="h-4 w-auto" aria-hidden="true" />
      <b className="line-clamp-2 max-w-full text-center text-base leading-tight">{opponent?.name}</b>
      <span className="block max-w-full truncate text-center text-[13px] leading-tight text-muted-foreground">Team {opponent?.id} · {opponent?.memberA} & {opponent?.memberB}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold leading-tight">{value}</div>
      <div className="leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

function AppMenu({
  activeView,
  title = "Pentathlon 2026",
  compact = false,
  setView,
  progress,
  startedAt,
  loading,
  startEvent,
  resetResults,
  resetClock,
  randomizeResults,
  gameMode,
  setGameMode
}: {
  activeView: View;
  title?: string;
  compact?: boolean;
  setView: (view: View) => void;
  progress: { done: number; total: number; percent: number };
  startedAt: string | null;
  loading: boolean;
  startEvent: () => void;
  resetResults: () => void;
  resetClock: () => void;
  randomizeResults: (preset: RandomFillPreset) => void;
  gameMode: GameMode;
  setGameMode: (gameMode: GameMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const clock = useMemo(() => makeClock(startedAt, now, progress.done, progress.total), [startedAt, now, progress.done, progress.total]);
  const items: Array<[View, string]> = [
    ["dashboard", "Dashboard"],
    ["matches", "Tabelle"],
    ["teams", "Teams"],
    ["ranking", "Ranking"],
    ["ranking-v2", "Ranking V2"]
  ];

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  function closeAndRun(action: () => void) {
    setOpen(false);
    action();
  }

  return (
    <>
      <div className={cn("flex items-center justify-between gap-2", compact && "justify-end")}>
        {compact ? null : <span className="truncate text-sm font-semibold">{title}</span>}
        <Button
          variant="ghost"
          size={compact ? "sm" : "icon-sm"}
          className="rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => setOpen(true)}
          title="Menü"
        >
          <Icon name={compact ? "settings" : "menu"} />
          {compact ? <span className="hidden sm:inline">Menü</span> : null}
        </Button>
      </div>
      {open ? createPortal(
        <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={() => setOpen(false)}>
          <aside className="h-full w-full max-w-sm overflow-y-auto bg-background p-4 shadow-xl ring-1 ring-black/10" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-sm font-semibold"><Icon name="menu" /> {title}</h2>
              <Button variant="ghost" size="icon-sm" onClick={() => setOpen(false)} title="Schließen"><Icon name="close" /></Button>
            </div>
            {!startedAt ? (
              <Button size="sm" className="mb-3 w-full" onClick={() => closeAndRun(startEvent)}>
                <Icon name="play_arrow" /> Pentathlon starten
              </Button>
            ) : null}
            <div className="rounded-lg bg-muted/45 p-3">
              <div className="mb-1 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
                {startedAt ? (
                  <div className="flex min-w-0 flex-col gap-1">
                    <span>Start {clock.startTime}</span>
                    <span>{clock.elapsed} gespielt</span>
                    <span>{loading ? "..." : `${progress.done}/${progress.total} Spiele`}</span>
                    <span>{clock.projection}</span>
                  </div>
                ) : (
                  <span>Pentathlon noch nicht gestartet</span>
                )}
                <span className="self-start tabular-nums">{progress.percent}%</span>
              </div>
              <Progress value={progress.percent} />
            </div>
            <div className="mt-3 rounded-lg bg-muted/45 p-3">
              <div className="text-sm font-medium">Ansicht</div>
              <div className="mt-3 grid grid-cols-1 gap-2">
                {items.map(([id, label]) => (
                  <Button
                    key={id}
                    variant={id === activeView ? "outline" : "secondary"}
                    size="sm"
                    className="justify-start"
                    onClick={() => closeAndRun(() => setView(id))}
                    title={label}
                  >
                    <Icon name={navIcons[id]} /> {label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-3 hidden rounded-lg bg-muted/45 p-3">
              <div className="text-sm font-medium">Ergebnisse zurücksetzen</div>
              <p className="mt-1 text-xs text-muted-foreground">Setzt alle Gewinner, Pluspunkte und Spielplanungen zurück.</p>
              <Button variant="destructive" size="sm" className="mt-3" onClick={() => closeAndRun(resetResults)}>
                <Icon name="restart_alt" /> Reset
              </Button>
            </div>
            <div className="mt-3 hidden rounded-lg bg-muted/45 p-3">
              <div className="text-sm font-medium">Zufallsdaten</div>
              <p className="mt-1 text-xs text-muted-foreground">Überschreibt Ergebnisse mit zufälligen Siegen und Niederlagen.</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {([50, 80, 99] as const).map((preset) => (
                  <Button key={preset} variant="secondary" size="sm" onClick={() => closeAndRun(() => randomizeResults(preset))}>
                    {preset === 99 ? "99%" : `${preset}%`}
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-3 rounded-lg bg-muted/45 p-3">
              <div className="text-sm font-medium">Spiel wechseln</div>
              <p className="mt-1 text-xs text-muted-foreground">Zeigt Mario Kart als Halligalli an.</p>
              <Button
                variant={gameMode === "halligalli" ? "default" : "secondary"}
                size="sm"
                className="mt-3"
                onClick={() => setGameMode(gameMode === "halligalli" ? "mario-kart" : "halligalli")}
              >
                <Icon name={gameMode === "halligalli" ? "notifications" : "directions_car"} /> {gameMode === "halligalli" ? "Halligalli" : "Mario Kart"}
              </Button>
            </div>
            <div className="mt-3 rounded-lg bg-muted/45 p-3">
              <div className="text-sm font-medium">Uhr zurücksetzen</div>
              <p className="mt-1 text-xs text-muted-foreground">Setzt die Startzeit zurück und zeigt den Startbutton wieder an.</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => closeAndRun(resetClock)}>
                <Icon name="timer_off" /> Uhr zurücksetzen
              </Button>
            </div>
          </aside>
        </div>,
        document.body
      ) : null}
    </>
  );
}

function RankingV2Page({
  state,
  progress,
  startedAt,
  loading,
  setView,
  startEvent,
  resetResults,
  resetClock,
  randomizeResults,
  gameMode,
  setGameMode,
  setTeamCostume
}: {
  state: AppState;
  progress: { done: number; total: number; percent: number };
  startedAt: string | null;
  loading: boolean;
  setView: (view: View) => void;
  startEvent: () => void;
  resetResults: () => void;
  resetClock: () => void;
  randomizeResults: (preset: RandomFillPreset) => void;
  gameMode: GameMode;
  setGameMode: (gameMode: GameMode) => void;
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  const clock = useMemo(() => makeClock(startedAt, now, progress.done, progress.total), [startedAt, now, progress.done, progress.total]);
  const rows = useMemo(() => makeRankingRows(state), [state.standings, state.teamCostumes, state.teams]);
  const leader = rows[0];
  const costumeCount = rows.filter((row) => row.costumeBonus).length;
  const totalWins = rows.reduce((sum, row) => sum + row.wins, 0);
  const totalBonus = rows.reduce((sum, row) => sum + row.bonus, 0);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="mx-auto flex h-full min-h-0 max-w-[1280px] flex-col gap-3 py-3">
      <div className="grid grid-cols-[minmax(220px,0.52fr)_minmax(0,1fr)_minmax(260px,0.58fr)] items-center gap-3">
        <Card className="justify-center rounded-none bg-transparent px-2 py-0 ring-0">
          <AppMenu
            activeView="ranking-v2"
            title="Ranking V2"
            setView={setView}
            progress={progress}
            startedAt={startedAt}
            loading={loading}
            startEvent={startEvent}
            resetResults={resetResults}
            resetClock={resetClock}
            randomizeResults={randomizeResults}
            gameMode={gameMode}
            setGameMode={setGameMode}
          />
        </Card>
        <div className="min-w-0 text-center">
          <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
            <Icon name="leaderboard" className="text-[16px]" /> Rangliste
          </div>
          <h1 className="truncate text-2xl font-black leading-tight">Pentathlon 2026 Ranking V2</h1>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <FinishMetric label="Spiele" value={`${progress.done}/${progress.total}`} />
          <FinishMetric label="Teams" value={rows.length} />
          <FinishMetric label="Kostüme" value={costumeCount} />
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(720px,1fr)_310px] gap-3">
        <div className="grid min-h-0 grid-rows-[minmax(250px,0.9fr)_minmax(360px,1.1fr)] gap-3">
          <Card className="min-h-0 overflow-hidden py-0">
            <CardHeader className="px-5 pb-0 pt-4">
              <CardTitle className="flex items-center justify-between text-lg">
                <span>Podest</span>
                <span className="text-xs font-semibold text-muted-foreground">{leader ? `${leader.rankingPoints} Pkt. vorne` : "Noch offen"}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid h-full min-h-0 grid-rows-[1fr_auto] px-5 pb-0 pt-2">
              <div className="grid min-h-0 grid-cols-[1fr_1.12fr_1fr] items-end gap-3">
                {[rows[1], rows[0], rows[2]].map((standing, index) => standing ? (
                  <RankingV2Podium
                    key={standing.teamId}
                    standing={standing}
                    position={index === 0 ? 2 : index === 1 ? 1 : 3}
                    setTeamCostume={setTeamCostume}
                  />
                ) : null)}
              </div>
              <div className="mt-3 grid grid-cols-2 border-t border-black/10 text-center text-sm font-semibold">
                <div className="border-b-2 border-foreground py-2">Punkte</div>
                <div className="py-2 text-muted-foreground">Disziplinen</div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-0 overflow-hidden py-0">
            <CardContent className="flex h-full min-h-0 flex-col p-0">
              <div className="grid grid-cols-[3rem_minmax(170px,1fr)_72px_84px_76px_92px_minmax(180px,0.95fr)_118px] border-b border-black/10 bg-muted/45 px-4 py-2 text-[10px] font-bold uppercase text-muted-foreground">
                <div>#</div>
                <div>Team</div>
                <div className="text-center">Pkt.</div>
                <div className="text-center">Spiele</div>
                <div className="text-center">Bilanz</div>
                <div className="text-center">Bonus</div>
                <div>Disziplinen</div>
                <div className="text-center">Team-Kostüm</div>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                {rows.map((standing, index) => (
                  <RankingV2TableRow
                    key={standing.teamId}
                    standing={standing}
                    position={index + 1}
                    matches={state.matches}
                    gameMode={gameMode}
                    setTeamCostume={setTeamCostume}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <aside className="grid min-h-0 grid-rows-[auto_auto_1fr] gap-3">
          <Card className="overflow-hidden py-0">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle className="text-base">Führendes Team</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4">
              {leader ? (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-xl font-black">{leader.team?.name ?? `Team ${leader.teamId}`}</div>
                      <div className="truncate text-xs text-muted-foreground">{leader.team?.memberA} & {leader.team?.memberB}</div>
                    </div>
                    <div className="grid size-14 place-items-center rounded-full bg-muted text-xl font-black tabular-nums ring-1 ring-black/10">#{leader.teamId}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <FinishMetric label="Ranking-Pkt." value={leader.rankingPoints} />
                    <FinishMetric label="Normal-Pkt." value={leader.points} />
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>

          <Card className="overflow-hidden py-0">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>Fortschritt</span>
                <span className="tabular-nums">{progress.percent}%</span>
              </div>
              <Progress value={progress.percent} />
              <div className="grid grid-cols-2 gap-2">
                <FinishMetric label="Siege" value={totalWins} />
                <FinishMetric label="Pluspunkte" value={totalBonus} />
              </div>
              <div className="rounded-md bg-muted/45 p-3 text-xs text-muted-foreground">
                <div>Start {clock.startTime}</div>
                <div>{clock.elapsed} gespielt</div>
                <div>{clock.projection}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="min-h-0 overflow-hidden py-0">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle className="text-base">Disziplin-Spitzen</CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-col gap-2 overflow-hidden px-4 pb-4">
              {DISCIPLINES.map((discipline) => (
                <RankingV2DisciplineLeader key={discipline} discipline={discipline} rows={rows} matches={state.matches} gameMode={gameMode} />
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>
    </section>
  );
}

function makeRankingRows(state: AppState): RankingStanding[] {
  return [...state.standings]
    .map((standing) => {
      const costumeBonus = Boolean(state.teamCostumes.find((item) => item.teamId === standing.teamId)?.enabled);
      return {
        ...standing,
        team: getTeam(state.teams, standing.teamId),
        costumeBonus,
        rankingPoints: standing.points + (costumeBonus ? 1 : 0)
      };
    })
    .sort((left, right) => right.rankingPoints - left.rankingPoints || right.points - left.points || right.wins - left.wins || left.losses - right.losses || left.teamId - right.teamId);
}

function RankingV2Podium({
  standing,
  position,
  setTeamCostume
}: {
  standing: RankingStanding;
  position: number;
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const height = position === 1 ? "h-28" : position === 2 ? "h-20" : "h-16";
  return (
    <div className="flex min-h-0 flex-col items-center justify-end gap-2">
      <div className="grid size-14 place-items-center rounded-full bg-background text-lg font-black tabular-nums ring-2 ring-black/10">
        {teamInitials(standing.team)}
      </div>
      <div className="min-w-0 text-center">
        <div className="truncate text-sm font-black">{standing.team?.name ?? `Team ${standing.teamId}`}</div>
        <div className="text-xs font-semibold text-muted-foreground">{standing.rankingPoints} Pkt.</div>
      </div>
      <TeamCostumeToggle standing={standing} onChange={setTeamCostume} compact />
      <div className={cn("flex w-full items-center justify-center rounded-t-md bg-muted/80 text-xl font-black tabular-nums ring-1 ring-inset ring-black/10", height)}>
        {position}.
      </div>
    </div>
  );
}

function RankingV2TableRow({
  standing,
  position,
  matches,
  gameMode,
  setTeamCostume
}: {
  standing: RankingStanding;
  position: number;
  matches: Match[];
  gameMode: GameMode;
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const teamMatches = matches.filter((match) => match.teamAId === standing.teamId || match.teamBId === standing.teamId);
  const completed = teamMatches.filter((match) => match.winnerTeamId !== null);
  const playedPercent = Math.round((standing.played / 15) * 100);
  return (
    <div className="grid min-h-[42px] grid-cols-[3rem_minmax(170px,1fr)_72px_84px_76px_92px_minmax(180px,0.95fr)_118px] items-center border-b border-black/6 px-4 text-[11px] odd:bg-muted/18">
      <div className={cn("font-black tabular-nums", position <= 3 ? "text-base" : "text-sm text-muted-foreground")}>{position}</div>
      <div className="flex min-w-0 items-center gap-2">
        <div className="grid size-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-black tabular-nums ring-1 ring-black/10">{teamInitials(standing.team)}</div>
        <div className="min-w-0">
          <div className="truncate text-sm font-bold leading-tight">{standing.team?.name ?? `Team ${standing.teamId}`}</div>
          <div className="truncate text-[10px] leading-tight text-muted-foreground">{standing.team?.memberA} & {standing.team?.memberB}</div>
        </div>
      </div>
      <div className="text-center text-lg font-black tabular-nums">{standing.rankingPoints}</div>
      <div className="text-center tabular-nums">
        <div className="font-bold">{standing.played}/15</div>
        <div className="mx-auto mt-1 h-1 w-12 overflow-hidden rounded bg-muted">
          <div className="h-full bg-foreground/55" style={{ width: `${playedPercent}%` }} />
        </div>
      </div>
      <div className="text-center font-bold tabular-nums">{standing.wins}-{standing.losses}</div>
      <div className="text-center text-[10px] tabular-nums">
        <span className="font-bold">+{standing.bonus}</span>
        {standing.costumeBonus ? <span className="ml-1 rounded bg-green-100 px-1 font-bold text-green-900">K+1</span> : null}
      </div>
      <div className="grid grid-cols-5 gap-1">
        {DISCIPLINES.map((discipline) => (
          <RankingV2DisciplineTrack key={discipline} discipline={discipline} matches={completed} teamId={standing.teamId} gameMode={gameMode} />
        ))}
      </div>
      <TeamCostumeToggle standing={standing} onChange={setTeamCostume} compact />
    </div>
  );
}

function RankingV2DisciplineTrack({
  discipline,
  matches,
  teamId,
  gameMode
}: {
  discipline: Discipline;
  matches: Match[];
  teamId: number;
  gameMode: GameMode;
}) {
  const disciplineMatches = matches.filter((match) => match.discipline === discipline);
  const wins = disciplineMatches.filter((match) => match.winnerTeamId === teamId).length;
  const bonus = disciplineMatches.filter((match) => match.bonusTeamId === teamId).length;
  const width = Math.min(100, ((wins + bonus) / 4) * 100);
  return (
    <div className="min-w-0" title={`${disciplineLabel(discipline, gameMode)}: ${wins} Siege${bonus ? `, ${bonus} Plus` : ""}`}>
      <div className="mb-0.5 flex items-center justify-center gap-0.5 text-[9px] font-bold tabular-nums">
        <Icon name={disciplineIcon(discipline, gameMode)} className="text-[10px] text-muted-foreground" />
        <span>{wins}</span>
      </div>
      <div className="h-1 overflow-hidden rounded bg-muted">
        <div className="h-full bg-foreground/50" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function RankingV2DisciplineLeader({
  discipline,
  rows,
  matches,
  gameMode
}: {
  discipline: Discipline;
  rows: RankingStanding[];
  matches: Match[];
  gameMode: GameMode;
}) {
  const leader = rows
    .map((row) => {
      const teamMatches = matches.filter((match) => match.discipline === discipline && (match.teamAId === row.teamId || match.teamBId === row.teamId));
      const wins = teamMatches.filter((match) => match.winnerTeamId === row.teamId).length;
      return { row, wins };
    })
    .sort((left, right) => right.wins - left.wins || left.row.teamId - right.row.teamId)[0];
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md bg-muted/35 px-3 py-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-bold">
          <Icon name={disciplineIcon(discipline, gameMode)} className="text-[14px]" />
          <span className="truncate">{disciplineLabel(discipline, gameMode)}</span>
        </div>
        <div className="truncate text-[10px] text-muted-foreground">{leader?.row.team?.name ?? "-"}</div>
      </div>
      <div className="text-right text-sm font-black tabular-nums">{leader?.wins ?? 0}</div>
    </div>
  );
}

function teamInitials(team?: Team) {
  if (!team) return "-";
  return team.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function RankingPage({
  state,
  progress,
  startedAt,
  loading,
  setView,
  startEvent,
  resetResults,
  resetClock,
  randomizeResults,
  gameMode,
  setGameMode,
  setTeamCostume
}: {
  state: AppState;
  progress: { done: number; total: number; percent: number };
  startedAt: string | null;
  loading: boolean;
  setView: (view: View) => void;
  startEvent: () => void;
  resetResults: () => void;
  resetClock: () => void;
  randomizeResults: (preset: RandomFillPreset) => void;
  gameMode: GameMode;
  setGameMode: (gameMode: GameMode) => void;
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  const clock = useMemo(() => makeClock(startedAt, now, progress.done, progress.total), [startedAt, now, progress.done, progress.total]);
  const rows = useMemo(() => {
    return [...state.standings]
      .map((standing) => {
        const costumeBonus = Boolean(state.teamCostumes.find((item) => item.teamId === standing.teamId)?.enabled);
        return {
          ...standing,
          team: getTeam(state.teams, standing.teamId),
          costumeBonus,
          rankingPoints: standing.points + (costumeBonus ? 1 : 0)
        };
      })
      .sort((left, right) => right.rankingPoints - left.rankingPoints || right.points - left.points || right.wins - left.wins || left.losses - right.losses || left.teamId - right.teamId);
  }, [state.standings, state.teamCostumes, state.teams]);
  const leader = rows[0];

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="flex h-full min-h-0 flex-col gap-2 py-1">
      <div className="grid grid-cols-[minmax(210px,0.6fr)_minmax(360px,1fr)_minmax(300px,0.85fr)] items-stretch gap-2">
        <Card className="justify-center rounded-none bg-transparent px-3 py-0 ring-0">
          <AppMenu
            activeView="ranking"
            title="Ranking"
            setView={setView}
            progress={progress}
            startedAt={startedAt}
            loading={loading}
            startEvent={startEvent}
            resetResults={resetResults}
            resetClock={resetClock}
            randomizeResults={randomizeResults}
            gameMode={gameMode}
            setGameMode={setGameMode}
          />
        </Card>
        <Card className="justify-center overflow-hidden border-transparent bg-transparent py-0 shadow-none ring-0">
          <CardContent className="flex h-full items-center justify-between gap-3 px-4 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
                <Icon name="emoji_events" className="text-[16px]" /> Podest
              </div>
              <h1 className="truncate text-2xl font-bold leading-tight">Pentathlon 2026 Ranking</h1>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <FinishMetric label="Spiele" value={`${progress.done}/${progress.total}`} />
              <FinishMetric label="Fortschritt" value={`${progress.percent}%`} />
              <FinishMetric label="Leader" value={leader?.team ? `#${leader.teamId}` : "-"} />
            </div>
          </CardContent>
        </Card>
        <Card className="justify-center overflow-hidden border-transparent bg-transparent py-0 shadow-none ring-0">
          <CardContent className="flex h-full items-center justify-between gap-3 px-4 py-2">
            <div className="min-w-0 text-xs text-muted-foreground">
              <div className="truncate">Start {clock.startTime}</div>
              <div className="truncate">{clock.elapsed} gespielt</div>
              <div className="truncate">{clock.projection}</div>
            </div>
            <Progress value={progress.percent} className="max-w-32" />
          </CardContent>
        </Card>
      </div>
      <Card className="min-h-0 flex-1 overflow-hidden bg-gradient-to-br from-white via-white to-muted/35 py-0">
        <CardContent className="grid h-full min-h-0 grid-cols-[minmax(390px,0.95fr)_minmax(560px,1.35fr)] gap-3 p-3">
          <div className="grid min-h-0 grid-rows-[1.24fr_1fr_0.9fr] gap-2">
            {rows.slice(0, 3).map((standing, index) => (
              <RankingPodiumCard
                key={standing.teamId}
                standing={standing}
                position={index + 1}
                matches={state.matches}
                gameMode={gameMode}
                setTeamCostume={setTeamCostume}
              />
            ))}
          </div>
          <div className="flex min-h-0 flex-col overflow-hidden rounded-lg bg-white/72 ring-1 ring-inset ring-black/8">
            <div className="grid grid-cols-[3.2rem_minmax(150px,1fr)_repeat(4,minmax(62px,0.38fr))_minmax(108px,0.55fr)_minmax(190px,0.95fr)] border-b border-black/8 bg-muted/45 px-3 py-2 text-[10px] font-bold uppercase text-muted-foreground">
              <div>Rang</div>
              <div>Team</div>
              <div className="text-center">Pkt.</div>
              <div className="text-center">Spiele</div>
              <div className="text-center">Siege</div>
              <div className="text-center">Plus</div>
              <div className="text-center">Team-Kostüm</div>
              <div className="grid grid-cols-5 gap-1 text-center">
                {DISCIPLINES.map((discipline) => (
                  <Icon key={discipline} name={disciplineIcon(discipline, gameMode)} className="mx-auto text-[13px]" />
                ))}
              </div>
            </div>
            <div className="grid min-h-0 flex-1 grid-rows-[repeat(13,minmax(0,1fr))]">
              {rows.slice(3).map((standing, index) => (
                <RankingTableRow
                  key={standing.teamId}
                  standing={standing}
                  position={index + 4}
                  matches={state.matches}
                  setTeamCostume={setTeamCostume}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function FinishMetric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-16 rounded-md bg-muted/50 px-2 py-1 tabular-nums">
      <div className="text-sm font-bold leading-tight">{value}</div>
      <div className="text-[9px] font-medium leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

function RankingPodiumCard({
  standing,
  position,
  matches,
  gameMode,
  setTeamCostume
}: {
  standing: RankingStanding;
  position: number;
  matches: Match[];
  gameMode: GameMode;
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const teamMatches = matches
    .filter((match) => match.teamAId === standing.teamId || match.teamBId === standing.teamId)
    .filter((match) => match.winnerTeamId !== null);
  const cardClass =
    position === 1
      ? "border-yellow-300 bg-yellow-50 shadow-xl shadow-yellow-200/55"
      : position === 2
        ? "border-slate-300 bg-slate-50 shadow-lg shadow-slate-200/45"
        : "border-orange-300 bg-orange-50 shadow-md shadow-orange-200/40";
  const rankClass =
    position === 1
      ? "bg-yellow-300 text-yellow-950 ring-yellow-400"
      : position === 2
        ? "bg-slate-300 text-slate-950 ring-slate-400"
        : "bg-orange-300 text-orange-950 ring-orange-400";
  const titleClass = position === 1 ? "text-4xl" : position === 2 ? "text-3xl" : "text-2xl";
  const label = position === 1 ? "Gold" : position === 2 ? "Silber" : "Bronze";

  return (
    <div className={cn("flex min-h-0 flex-col justify-between overflow-hidden rounded-lg border p-3 ring-1 ring-black/5", cardClass)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-1 text-[10px] font-black uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={cn("truncate pb-1 font-black leading-tight", titleClass)}>{standing.team?.name ?? `Team ${standing.teamId}`}</div>
          <div className="mt-1 truncate text-xs font-medium text-muted-foreground">Team {standing.teamId} · {standing.team?.memberA} & {standing.team?.memberB}</div>
        </div>
        <div className={cn("flex shrink-0 items-center justify-center rounded-full font-black tabular-nums ring-1", position === 1 ? "size-16 text-3xl" : "size-13 text-2xl", rankClass)}>
          {position === 1 ? <Icon name="emoji_events" className="text-[34px]" /> : position}
        </div>
      </div>
      <div className="grid grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr] gap-2">
        <RankingCell strong value={standing.rankingPoints} label="Pkt." />
        <RankingCell value={`${standing.played}/15`} label="Spiele" />
        <RankingCell value={standing.wins} label="Siege" />
        <RankingCell value={standing.bonus} label="Plus" />
      </div>
      <TeamCostumeToggle standing={standing} onChange={setTeamCostume} featured />
      <div className="grid grid-cols-5 gap-1.5">
        {DISCIPLINES.map((discipline) => (
          <DisciplineScore
            key={discipline}
            discipline={discipline}
            matches={teamMatches}
            teamId={standing.teamId}
            icon={disciplineIcon(discipline, gameMode)}
          />
        ))}
      </div>
    </div>
  );
}

function RankingCell({ value, label, strong = false }: { value: React.ReactNode; label: string; strong?: boolean }) {
  return (
    <div className="rounded-md bg-white/70 px-2 py-1 text-center tabular-nums ring-1 ring-inset ring-black/5">
      <div className={cn("leading-tight", strong ? "text-2xl font-black" : "text-lg font-bold")}>{value}</div>
      <div className="text-[9px] font-medium leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

function RankingTableRow({
  standing,
  position,
  matches,
  setTeamCostume
}: {
  standing: RankingStanding;
  position: number;
  matches: Match[];
  setTeamCostume: (teamId: number, enabled: boolean) => void;
}) {
  const teamMatches = matches
    .filter((match) => match.teamAId === standing.teamId || match.teamBId === standing.teamId)
    .filter((match) => match.winnerTeamId !== null);

  return (
    <div className="grid min-h-0 grid-cols-[3.2rem_minmax(150px,1fr)_repeat(4,minmax(62px,0.38fr))_minmax(108px,0.55fr)_minmax(190px,0.95fr)] items-center gap-0 border-b border-black/6 px-3 text-[11px] odd:bg-muted/18">
      <div>
        <div className="flex size-7 items-center justify-center rounded-full bg-background text-xs font-bold tabular-nums text-muted-foreground ring-1 ring-black/10">{position}</div>
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold leading-tight">{standing.team?.name ?? `Team ${standing.teamId}`}</div>
        <div className="truncate text-[9px] leading-tight text-muted-foreground">Team {standing.teamId}</div>
      </div>
      <CompactRankingCell strong value={standing.rankingPoints} />
      <CompactRankingCell value={`${standing.played}/15`} />
      <CompactRankingCell value={standing.wins} />
      <CompactRankingCell value={standing.bonus} />
      <TeamCostumeToggle standing={standing} onChange={setTeamCostume} compact />
      <div className="grid grid-cols-5 gap-1">
        {DISCIPLINES.map((discipline) => (
          <DisciplineScore key={discipline} discipline={discipline} matches={teamMatches} teamId={standing.teamId} compact />
        ))}
      </div>
    </div>
  );
}

function TeamCostumeToggle({
  standing,
  onChange,
  compact = false,
  featured = false
}: {
  standing: RankingStanding;
  onChange: (teamId: number, enabled: boolean) => void;
  compact?: boolean;
  featured?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer select-none items-center justify-center gap-1.5 rounded-md text-center font-semibold transition-colors",
        standing.costumeBonus ? "bg-green-100 text-green-900 ring-1 ring-inset ring-green-300" : "bg-white/55 text-muted-foreground ring-1 ring-inset ring-black/8 hover:bg-muted/60",
        compact ? "mx-1 px-1.5 py-1 text-[9px]" : "px-2 py-1.5 text-xs",
        featured && "justify-start"
      )}
      title="Team-Kostüm"
    >
      <input
        type="checkbox"
        className="size-3.5 accent-green-700"
        checked={standing.costumeBonus}
        onChange={(event) => onChange(standing.teamId, event.target.checked)}
      />
      <span className="truncate">Team-Kostüm</span>
      {standing.costumeBonus ? <span className="shrink-0 tabular-nums">+1</span> : null}
    </label>
  );
}

function CompactRankingCell({ value, strong = false }: { value: React.ReactNode; strong?: boolean }) {
  return <div className={cn("text-center tabular-nums", strong ? "text-base font-black" : "text-sm font-bold")}>{value}</div>;
}

function DisciplineScore({
  discipline,
  matches,
  teamId,
  icon,
  compact = false
}: {
  discipline: Discipline;
  matches: Match[];
  teamId: number;
  icon?: string;
  compact?: boolean;
}) {
  const disciplineMatches = matches.filter((match) => match.discipline === discipline);
  const wins = disciplineMatches.filter((match) => match.winnerTeamId === teamId).length;
  const bonus = disciplineMatches.filter((match) => match.bonusTeamId === teamId).length;
  return (
    <div className={cn("min-w-0", compact ? "" : "px-0")}>
      <div className={cn("flex items-center justify-center gap-1 rounded bg-background/70 text-center tabular-nums ring-1 ring-inset ring-black/5", compact ? "px-1 py-0.5" : "px-1.5 py-1")}>
        {icon ? <Icon name={icon} className="text-[13px] text-muted-foreground" /> : null}
        <span className={cn("font-semibold leading-none", compact ? "text-xs" : "text-sm")}>{wins}</span>
        <span className="text-[9px] leading-none text-muted-foreground">S</span>
        {bonus ? <span className="rounded bg-green-100 px-1 text-[9px] font-bold leading-tight text-green-800">+{bonus}</span> : null}
      </div>
    </div>
  );
}

function Matches({
  state,
  patchMatch,
  patchDisciplineState,
  setView,
  progress,
  startedAt,
  loading,
  startEvent,
  resetResults,
  resetClock,
  randomizeResults,
  gameMode,
  setGameMode
}: {
  state: AppState;
  patchMatch: (matchId: number, winnerTeamId: number | null, bonus: boolean) => void;
  patchDisciplineState: (discipline: Discipline, activeMatchId: number | null, nextMatchId: number | null) => void;
  setView: (view: View) => void;
  progress: { done: number; total: number; percent: number };
  startedAt: string | null;
  loading: boolean;
  startEvent: () => void;
  resetResults: () => void;
  resetClock: () => void;
  randomizeResults: (preset: RandomFillPreset) => void;
  gameMode: GameMode;
  setGameMode: (gameMode: GameMode) => void;
}) {
  const [hovered, setHovered] = useState<{ teamId: number; discipline: Discipline; slotIndex?: number; matchId?: number } | null>(null);

  return (
    <section className="flex h-full flex-col pt-1">
      <Card className="min-h-0 flex-1 overflow-hidden rounded-none bg-transparent py-0 ring-0">
        <CardContent className="h-full p-0">
          <div className="h-full overflow-x-auto overflow-y-auto">
            <table className="h-full w-full min-w-[1604px] table-fixed border-separate border-spacing-0 text-[11px]">
              <colgroup>
                <col className="w-14" />
                <col className="w-[150px]" />
                {DISCIPLINES.map((discipline) => (
                  <React.Fragment key={discipline}>
                    <col className="w-2.5" />
                    <col className="w-[76px]" />
                    <col className="w-[76px]" />
                    <col className="w-[76px]" />
                    <col className="w-8" />
                  </React.Fragment>
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 border-b border-b-black/20 bg-card px-2 py-1 text-left font-medium" colSpan={2}>
                    <AppMenu
                      activeView="matches"
                      setView={setView}
                      progress={progress}
                      startedAt={startedAt}
                      loading={loading}
                      startEvent={startEvent}
                      resetResults={resetResults}
                      resetClock={resetClock}
                      randomizeResults={randomizeResults}
                      gameMode={gameMode}
                      setGameMode={setGameMode}
                    />
                  </th>
                  {DISCIPLINES.map((discipline) => {
                    const done = state.matches.filter((match) => match.discipline === discipline && match.winnerTeamId !== null).length;
                    return (
                      <React.Fragment key={discipline}>
                        <th className="bg-background" />
                        <th className="rounded-t-lg border-x border-t border-b border-black/20 bg-muted/60 px-2 py-1 text-left font-medium" colSpan={4}>
                          <span className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5"><Icon name={disciplineIcon(discipline, gameMode)} className="text-[16px]" /> {disciplineLabel(discipline, gameMode)}</span>
                            <Badge variant="secondary">{done}/24</Badge>
                          </span>
                        </th>
                      </React.Fragment>
                    );
                  })}
                </tr>
                <tr className="text-[10px] text-muted-foreground">
                  <th className="sticky left-0 z-20 border-b border-b-black/20 bg-card px-2 py-1 text-left font-medium">ID</th>
                  <th className="border-b border-b-black/20 bg-card px-2 py-1 text-left font-medium">Team</th>
                  {DISCIPLINES.map((discipline) => (
                    <React.Fragment key={discipline}>
                      <th className="bg-background" />
                      <th className="border-l border-b border-l-black/20 border-b-black/20 bg-card px-1 py-1 text-center font-medium">B1</th>
                      <th className="border-b border-b-black/20 bg-card px-1 py-1 text-center font-medium">B2</th>
                      <th className="border-b border-b-black/20 bg-card px-1 py-1 text-center font-medium">B3</th>
                      <th className="border-r border-b border-r-black/20 border-b-black/20 bg-card px-1 py-1 text-center font-medium">PP</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.teams.map((team) => {
                  const isRowHovered = hovered?.teamId === team.id;
                  const standing = state.standings.find((item) => item.teamId === team.id);
                  const isPlaying = isTeamInState(team.id, state, "active");
                  const isQueued = isTeamInState(team.id, state, "next");
                  return (
                  <tr key={team.id} className={cn("odd:bg-muted/20", isRowHovered && "bg-[color-mix(in_oklch,var(--background)_98%,var(--primary)_2%)]")}>
                    <td className="sticky left-0 z-10 border-b border-b-black/15 bg-inherit px-1.5 py-0.5 text-center align-middle font-semibold tabular-nums">
                      <span className="flex min-h-[42px] flex-col items-center justify-center gap-0.5">
                        <span className="group/team relative inline-block cursor-default text-base leading-none">
                          {team.id}
                          <TeamTooltip team={team} />
                        </span>
                        <span className="flex min-h-[20px] flex-col items-center justify-start gap-0.5">
                          {isPlaying ? <span className="w-full rounded bg-green-50 px-0.5 text-[7px] font-semibold uppercase leading-tight text-green-700 ring-1 ring-inset ring-green-200">Spielt</span> : null}
                          {isQueued ? <span className="w-full rounded bg-blue-50 px-0.5 text-[7px] font-semibold uppercase leading-tight text-blue-700 ring-1 ring-inset ring-blue-200">Nächstes</span> : null}
                        </span>
                      </span>
                    </td>
                    <td className="max-w-40 border-b border-b-black/15 px-2 py-0.5 align-middle">
                      <span className="block max-w-full">
                        <span className="block truncate font-medium">
                        {team.name}
                        </span>
                        <span className="block truncate text-xs font-normal leading-tight text-muted-foreground">{team.memberA} & {team.memberB}</span>
                        <span className="block truncate text-[11px] font-normal leading-tight text-muted-foreground">
                          {standing?.played ?? 0}/15 Spiele · {standing?.wins ?? 0}/{standing?.played ?? 0} W · {standing?.bonus ?? 0} PP
                        </span>
                      </span>
                    </td>
                    {DISCIPLINES.map((discipline) => {
                      const teamMatches = matchesForTeamDiscipline(state.matches, team.id, discipline);
                      return (
                        <React.Fragment key={discipline}>
                          <td className="bg-background" />
                          {[0, 1, 2].map((index) => {
                            const match = teamMatches[index];
                            const isColumnHovered = hovered?.discipline === discipline && hovered.slotIndex === index;
                            const isRelatedMatch = Boolean(match && hovered?.matchId === match.id);
                            const isCenteredCell = hovered?.teamId === team.id && hovered?.discipline === discipline && hovered?.slotIndex === index;
                            const disciplineState = state.disciplineStates.find((item) => item.discipline === discipline);
                            const isNextCell = Boolean(match && disciplineState?.nextMatchId === match.id);
                            const isActiveCell = Boolean(match && disciplineState?.activeMatchId === match.id);
                            return (
                              <td
                                key={index}
                                className={cn(
                                "border-b border-b-black/15 p-0 align-middle transition-colors",
                                index === 0 && "border-l border-l-black/20",
                                index > 0 && "border-l border-l-background",
                                index === 0 && team.id === 1 && "rounded-tl-md",
                                index === 0 && team.id === 16 && "rounded-bl-md",
                                team.id === 16 && "border-b-black/20",
                                  isColumnHovered && "bg-blue-50/25",
                                  isRelatedMatch && "bg-blue-100/70",
                                  isCenteredCell && "bg-blue-100",
                                  isNextCell && "bg-blue-100",
                                  isActiveCell && "bg-green-100"
                                )}
                                onMouseEnter={() => setHovered({ teamId: team.id, discipline, slotIndex: index, matchId: match?.id })}
                                onMouseLeave={() => setHovered(null)}
                              >
                                {match ? (
                                <MatchCell
                                  team={team}
                                  match={match}
                                  discipline={discipline}
                                  state={state}
                                  patchMatch={patchMatch}
                                  patchDisciplineState={patchDisciplineState}
                                />
                              ) : (
                                <span className="block text-center text-muted-foreground">-</span>
                              )}
                              </td>
                            );
                          })}
                          <td
                            className={cn(
                              "relative border-r border-b border-l border-r-black/20 border-b-black/15 border-l-[var(--background)] p-0 text-center align-middle transition-colors",
                              team.id === 1 && "rounded-tr-md",
                              team.id === 16 && "rounded-br-md border-b-black/20"
                            )}
                            onMouseEnter={() => setHovered({ teamId: team.id, discipline })}
                            onMouseLeave={() => setHovered(null)}
                          >
                            <BonusCell team={team} discipline={discipline} state={state} patchMatch={patchMatch} />
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function MatchCell({
  team,
  match,
  discipline,
  state,
  patchMatch,
  patchDisciplineState
}: {
  team: Team;
  match: Match;
  discipline: Discipline;
  state: AppState;
  patchMatch: (matchId: number, winnerTeamId: number | null, bonus: boolean) => void;
  patchDisciplineState: (discipline: Discipline, activeMatchId: number | null, nextMatchId: number | null) => void;
}) {
  const disciplineState = state.disciplineStates.find((item) => item.discipline === discipline) ?? { discipline, activeMatchId: null, nextMatchId: null };
  const opponent = getTeam(state.teams, match.teamAId === team.id ? match.teamBId : match.teamAId);
  const isActive = disciplineState.activeMatchId === match.id;
  const isNext = disciplineState.nextMatchId === match.id;
  const result = match.winnerTeamId === null ? "-" : match.winnerTeamId === team.id ? "W" : "L";
  const hasResult = match.winnerTeamId !== null;
  const nextDisabled = hasResult || (!isNext && isNextBlocked(match, discipline, state));
  const activeDisabled = hasResult || (!isActive && isActiveBlocked(match, discipline, state));
  const nextTitle = hasResult ? "Ergebnis ist bereits eingetragen" : nextDisabled ? "Ein Team ist bereits als nächstes eingeplant" : "Als nächstes";
  const activeTitle = hasResult ? "Ergebnis ist bereits eingetragen" : activeDisabled ? "Ein Team spielt bereits in einer anderen Begegnung" : "Spielt gerade";

  function setSchedule(kind: "next" | "active") {
    if (match.winnerTeamId !== null) return;
    if (kind === "next") {
      if (isNext) {
        patchDisciplineState(discipline, disciplineState.activeMatchId, null);
        return;
      }
      if (!isNextBlocked(match, discipline, state)) {
        patchDisciplineState(discipline, isActive ? null : disciplineState.activeMatchId, match.id);
      }
      return;
    }
    if (isActive) {
      patchDisciplineState(discipline, null, disciplineState.nextMatchId);
      return;
    }
    if (!isActiveBlocked(match, discipline, state)) {
      patchDisciplineState(discipline, match.id, isNext ? null : disciplineState.nextMatchId);
    }
  }

  function setResult(kind: "win" | "loss") {
    const opponentId = opponent?.id ?? null;
    const winnerId = kind === "win" ? team.id : opponentId;
    const nextWinner = match.winnerTeamId === winnerId ? null : winnerId;
    patchMatch(match.id, nextWinner, nextWinner !== null && match.bonusTeamId === nextWinner);
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-8 flex-col items-stretch rounded pt-1 transition-colors",
        match.winnerTeamId !== null && "opacity-75"
      )}
    >
      <div className="mb-1.5 px-1.5 text-center text-base font-bold leading-none tabular-nums">
        <span className="group/team relative inline-block cursor-default">
          {opponent?.id}
        {opponent ? <TeamTooltip team={opponent} /> : null}
        </span>
      </div>
      <div className="mt-auto grid w-full grid-cols-4 overflow-hidden rounded-b border-t border-black/20">
          <button
            disabled={nextDisabled}
            className={cn(
              "h-7 cursor-pointer px-1 text-center text-[8px] font-bold uppercase tabular-nums transition-colors",
              isNext && "bg-blue-200 text-blue-950",
              !isNext && "bg-background text-muted-foreground hover:bg-muted",
              nextDisabled && "cursor-not-allowed opacity-40 hover:bg-background"
            )}
            onClick={(event) => {
              event.stopPropagation();
              setSchedule("next");
            }}
            title={nextTitle}
          >
            N
          </button>
          <button
            disabled={activeDisabled}
            className={cn(
              "h-7 cursor-pointer border-l border-black/20 px-1 text-center text-[8px] font-bold uppercase tabular-nums transition-colors",
              isActive && "bg-green-200 text-green-950",
              !isActive && "bg-background text-muted-foreground hover:bg-muted",
              activeDisabled && "cursor-not-allowed opacity-40 hover:bg-background"
            )}
            onClick={(event) => {
              event.stopPropagation();
              setSchedule("active");
            }}
            title={activeTitle}
          >
            S
          </button>
          <button
            className={cn(
              "h-7 cursor-pointer border-l border-black/20 px-1 text-center text-[9px] font-bold tabular-nums transition-colors",
              result === "W" && "bg-green-200 text-green-950",
              result !== "W" && "bg-background text-muted-foreground hover:bg-muted"
            )}
            onClick={(event) => {
              event.stopPropagation();
              setResult("win");
            }}
            title="Win"
          >
            W
          </button>
          <button
            className={cn(
              "h-7 cursor-pointer border-l border-black/20 px-1 text-center text-[9px] font-bold tabular-nums transition-colors",
              result === "L" && "bg-red-200 text-red-950",
              result !== "L" && "bg-background text-muted-foreground hover:bg-muted"
            )}
            onClick={(event) => {
              event.stopPropagation();
              setResult("loss");
            }}
            title="Loss"
          >
            L
          </button>
      </div>
    </div>
  );
}

function BonusCell({ team, discipline, state, patchMatch }: { team: Team; discipline: Discipline; state: AppState; patchMatch: (matchId: number, winnerTeamId: number | null, bonus: boolean) => void }) {
  const matches = matchesForTeamDiscipline(state.matches, team.id, discipline);
  const wonMatches = matches.filter((match) => match.winnerTeamId === team.id);
  const bonusMatch = matches.find((match) => match.bonusTeamId === team.id);
  const target = bonusMatch ?? wonMatches[0];
  return (
    <button
      title="Pluspunkt"
      aria-label={`Pluspunkt ${team.name} ${discipline}`}
      className={cn(
        "absolute inset-0 flex h-full w-full items-center justify-center rounded text-[11px] font-bold tabular-nums transition-colors",
        bonusMatch && "bg-green-200 text-green-950 ring-1 ring-inset ring-green-300 shadow-sm",
        !bonusMatch && "cursor-pointer bg-transparent text-muted-foreground hover:bg-muted",
        !target && "cursor-not-allowed opacity-40 hover:bg-transparent"
      )}
      disabled={!target}
      onClick={() => target ? patchMatch(target.id, team.id, !bonusMatch) : undefined}
    >
      {bonusMatch ? "+" : "-"}
    </button>
  );
}

function matchesForTeamDiscipline(matches: Match[], teamId: number, discipline: Discipline) {
  return matches
    .filter((match) => match.discipline === discipline && (match.teamAId === teamId || match.teamBId === teamId))
    .sort((left, right) => left.slot - right.slot);
}

function isTeamInState(teamId: number, state: AppState, kind: "active" | "next") {
  return state.disciplineStates.some((disciplineState) => {
    const matchId = kind === "active" ? disciplineState.activeMatchId : disciplineState.nextMatchId;
    const match = state.matches.find((candidate) => candidate.id === matchId);
    return Boolean(match && (match.teamAId === teamId || match.teamBId === teamId));
  });
}

function TeamTooltip({ team }: { team: Team }) {
  return (
    <span className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1 hidden w-44 -translate-x-1/2 rounded-lg bg-foreground px-2 py-1.5 text-left text-xs font-normal text-background shadow-lg group-hover/team:block">
      <span className="block font-semibold">{team.id} · {team.name}</span>
      <span className="block opacity-80">{team.memberA} & {team.memberB}</span>
    </span>
  );
}

function Teams({ state, saveTeams }: { state: AppState; saveTeams: (teams: Team[]) => void }) {
  const [teams, setTeams] = useState(state.teams);

  function update(id: number, field: keyof Team, value: string) {
    setTeams((current) => current.map((team) => (team.id === id ? { ...team, [field]: field === "id" ? Number(value) : value } : team)));
  }

  return (
    <section className="space-y-4 pt-1">
      <div className="flex items-center justify-end">
        <Button size="sm" onClick={() => saveTeams(teams)}><Icon name="save" /> Speichern</Button>
      </div>
      <Card className="overflow-hidden py-0">
        <CardContent className="p-0">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="border-b border-black/10 px-3 py-2 font-medium">ID</th>
                <th className="border-b border-black/10 px-3 py-2 font-medium">Team</th>
                <th className="border-b border-black/10 px-3 py-2 font-medium">Spieler 1</th>
                <th className="border-b border-black/10 px-3 py-2 font-medium">Spieler 2</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} className="odd:bg-muted/25 hover:bg-[color-mix(in_oklch,var(--background)_93%,var(--primary)_7%)]">
                  <td className="w-16 border-b border-black/6 px-3 py-1.5 font-semibold tabular-nums">{team.id}</td>
                  <td className="border-b border-black/6 px-2 py-1.5">
                    <Input className="h-8" value={team.name} onChange={(event) => update(team.id, "name", event.target.value)} aria-label={`Team ${team.id} Name`} />
                  </td>
                  <td className="border-b border-black/6 px-2 py-1.5">
                    <Input className="h-8" value={team.memberA} onChange={(event) => update(team.id, "memberA", event.target.value)} aria-label={`Team ${team.id} Person A`} />
                  </td>
                  <td className="border-b border-black/6 px-2 py-1.5">
                    <Input className="h-8" value={team.memberB} onChange={(event) => update(team.id, "memberB", event.target.value)} aria-label={`Team ${team.id} Person B`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </section>
  );
}

function isNextBlocked(match: Match, discipline: Discipline, state: AppState) {
  const blockedTeamIds = new Set<number>();
  for (const item of state.disciplineStates) {
    if (item.discipline === discipline || item.nextMatchId === null) continue;
    const nextMatch = state.matches.find((candidate) => candidate.id === item.nextMatchId);
    if (!nextMatch) continue;
    blockedTeamIds.add(nextMatch.teamAId);
    blockedTeamIds.add(nextMatch.teamBId);
  }
  return blockedTeamIds.has(match.teamAId) || blockedTeamIds.has(match.teamBId);
}

function isActiveBlocked(match: Match, discipline: Discipline, state: AppState) {
  for (const item of state.disciplineStates) {
    if (item.discipline === discipline || item.activeMatchId === null) continue;
    const activeMatch = state.matches.find((candidate) => candidate.id === item.activeMatchId);
    if (!activeMatch) continue;
    if ([match.teamAId, match.teamBId].includes(activeMatch.teamAId) || [match.teamAId, match.teamBId].includes(activeMatch.teamBId)) {
      return true;
    }
  }
  return false;
}

function getTeam(teams: Team[], id: number) {
  return teams.find((team) => team.id === id);
}

createRoot(document.getElementById("root")!).render(<App />);
